'use client';

import React from 'react';
import { type ChangeEvent, type FieldsetHTMLAttributes, type ForwardedRef, forwardRef, useState } from 'react';

import clsx from 'clsx';

import { NeedHelp } from '../Icons';

import styles from './RadioGroup.module.scss';

type RadioOption = {
  label: React.ReactNode;
  value: string;
  helpText?: React.ReactNode;
};

interface RadioInputProps {
  option: RadioOption;
  isSelected?: boolean;
  isDisabled?: boolean;
  errorText?: React.ReactNode;
  name?: string;
  handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

const RadioInput = ({
  option,
  isSelected = false,
  isDisabled = false,
  errorText,
  name = '',
  handleChange,
}: RadioInputProps) => {
  const [showHelp, setShowHelp] = useState(false);

  const radioId = `${name}${option.value}`;

  return (
    <div
      className={clsx(styles.radio, {
        [styles.disabled]: isDisabled,
        [styles.error]: Boolean(errorText),
      })}
      key={radioId}
    >
      <input
        checked={isSelected}
        className={clsx(styles.input, { [styles.disabled]: isDisabled, [styles.error]: Boolean(errorText) })}
        id={radioId}
        name={name}
        onChange={(e) => handleChange(e)}
        type="radio"
        value={option.value}
      />
      <label
        className={clsx(styles.label, { [styles.disabled]: isDisabled, [styles.error]: Boolean(errorText) })}
        htmlFor={radioId}
      >
        {option.label}
      </label>

      {option.helpText && (
        <div className={clsx(styles['help-container'])}>
          <a
            onClick={(event) => {
              event.stopPropagation();
              setShowHelp(!showHelp);
            }}
            onMouseEnter={() => setShowHelp(!showHelp)}
            onMouseLeave={() => setShowHelp(!showHelp)}
            className={clsx(styles['help-link'])}
          >
            <NeedHelp className={clsx(styles['help-icon'])} />
          </a>
          {showHelp && <div className={clsx(styles['help-text'])}>{option.helpText}</div>}
        </div>
      )}
    </div>
  );
};

type RadioGroupProps = FieldsetHTMLAttributes<HTMLFieldSetElement> & {
  /**
   * The translated string to use when there is an error with the radio group.
   */
  errorText?: React.ReactNode;

  /**
   * The state to represent if the radio group should be disabled.
   */
  isDisabled?: boolean;

  /**
   * The translated string to use as the radio group's label.
   */
  legend: React.ReactNode;

  /**
   * Hide the legend visually, but keep it accessible for screen readers.
   */
  hideLegend?: boolean;

  /**
   * The choices for each radio group.
   */
  options: Array<RadioOption>;

  wrapOptions?: boolean;

  centerOptions?: boolean;

  compact?: boolean;

  compactLegend?: boolean;

  orientation?: 'horizontal' | 'vertical';

  /**
   * The selected choice from the radio group.
   */
  selectedOptionValue?: string;

  /**
   * The function used to set the selected choice for the radio group.
   */
  setSelectedOptionValue: (selectedOptionValue: string) => void;
};

export const RadioGroup = forwardRef(
  (
    {
      className,
      errorText,
      isDisabled,
      legend,
      hideLegend = false,
      name,
      onChange,
      options,
      wrapOptions = false,
      centerOptions = false,
      compact = false,
      compactLegend = false,
      orientation = 'horizontal',
      selectedOptionValue,
      setSelectedOptionValue,
      ...radioGroupProps
    }: RadioGroupProps,
    ref: ForwardedRef<HTMLFieldSetElement>,
  ) => {
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (setSelectedOptionValue) {
        setSelectedOptionValue(event.target.value);
      }
    };

    return (
      <fieldset
        {...radioGroupProps}
        className={clsx(
          styles.container,
          {
            [styles.compact]: compact,
          },
          className,
        )}
        disabled={isDisabled}
        ref={ref}
      >
        <legend className={clsx(styles.hidden)}>{legend}</legend>

        <div
          aria-hidden="true"
          className={clsx(styles.legend, {
            [styles.disabled]: isDisabled,
            [styles.error]: Boolean(errorText),
            [styles.hidden]: hideLegend,
            [styles['compact-legend']]: compactLegend,
          })}
        >
          {legend}
        </div>

        <div
          className={clsx(styles.options, {
            [styles.vertical]: orientation === 'vertical',
            [styles.wrapped]: wrapOptions,
            [styles.centered]: centerOptions,
          })}
        >
          {options.map((option, index) => (
            <RadioInput
              key={option.value}
              option={option}
              isSelected={selectedOptionValue === option.value}
              isDisabled={isDisabled}
              errorText={errorText}
              name={name}
              handleChange={handleChange}
            />
          ))}
        </div>

        {Boolean(errorText) && <div className={styles['error-text']}>{errorText}</div>}
      </fieldset>
    );
  },
);
